import React, { useState } from 'react';
import styled from 'styled-components';
import { VisuallyHidden } from '../VisuallyHidden';
import { Caption } from '../Typography';
export const SegmentedRadioGroup = ({ name, options, value, defaultValue, onChange, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, }) => {
    const [internalValue, setInternalValue] = useState(defaultValue || '');
    const currentValue = value !== undefined ? value : internalValue;
    const handleChange = (newValue) => {
        if (value === undefined) {
            setInternalValue(newValue);
        }
        onChange === null || onChange === void 0 ? void 0 : onChange(newValue);
    };
    return (React.createElement(StyledFieldset, { role: "radiogroup", "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy },
        React.createElement(VisuallyHidden, { as: "legend" }, ariaLabel || 'Radio button group'),
        React.createElement(StyledContainer, null, options.map((option, index) => {
            const isSelected = currentValue === option.value;
            const isFirst = index === 0;
            const isLast = index === options.length - 1;
            return (React.createElement(StyledLabel, { key: option.value, "$isSelected": isSelected, "$disabled": option.disabled, "$isFirst": isFirst, "$isLast": isLast, "data-selected": isSelected },
                React.createElement(StyledInput, { type: "radio", name: name, value: option.value, checked: isSelected, disabled: option.disabled, onChange: (e) => handleChange(e.target.value), "aria-describedby": `${name}-${option.value}` }),
                React.createElement(StyledCaption, { as: "span", id: `${name}-${option.value}`, mb: "0", title: option.title, "$isSelected": isSelected }, option.label)));
        }))));
};
const StyledFieldset = styled.fieldset `
  border: none;
  margin: 0;
  padding: 0;
`;
const StyledContainer = styled.div `
  display: flex;
  width: fit-content;
  border: 1px solid ${({ theme }) => theme.colors.grey.medium};
  border-radius: 0.8rem;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.white};
`;
const StyledLabel = styled.label `
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.8rem 1.6rem;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  background: ${({ $isSelected, $disabled, theme }) => {
    if ($disabled)
        return theme.colors.grey.light;
    if ($isSelected)
        return theme.colors.background.dark;
    return theme.colors.white;
}};
  border: 2px solid transparent;
  transition: all 0.2s ease;
  z-index: 1;
  margin-left: ${({ $isFirst }) => ($isFirst ? '0' : '-0.2rem')};
  min-width: 0;

  /* Separator line on the right side of each button except the last */
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    right: 0;
    top: 0.1rem;
    bottom: 0.1rem;
    width: 1px;
    background: ${({ $isSelected, theme }) => ($isSelected ? 'transparent' : theme.colors.grey.medium)};
    transition: background 0.2s ease;
    z-index: 0;
  }

  /* Hide separator when next button is selected */
  &:not(:last-child):has(+ label[data-selected='true'])::after {
    background: transparent;
  }

  &:hover {
    background: ${({ $isSelected, $disabled, theme }) => {
    if ($disabled)
        return theme.colors.grey.light;
    if ($isSelected)
        return theme.colors.background.dark;
    return theme.colors.background.medium;
}};
    border: 2px solid ${({ $disabled, theme }) => ($disabled ? 'transparent' : theme.colors.grey.dark)};
    z-index: 3;
    border-radius: ${({ $isFirst, $isLast }) => {
    if ($isFirst && $isLast)
        return '0.6rem'; // Single button
    if ($isFirst)
        return '0.6rem 0 0 0.6rem'; // First button - left rounded
    if ($isLast)
        return '0 0.6rem 0.6rem 0'; // Last button - right rounded
    return '0'; // Middle buttons - no radius
}};

    /* Hide separator on hover */
    &::after {
      background: transparent;
    }
  }

  /* Hide separator when next button is hovered */
  &:not(:last-child):has(+ label:hover)::after {
    background: transparent;
  }

  &:focus-within {
    z-index: 3;
    border: 2px solid #000;
    border-radius: ${({ $isFirst, $isLast }) => {
    if ($isFirst && $isLast)
        return '0.6rem';
    if ($isFirst)
        return '0.6rem 0 0 0.6rem';
    if ($isLast)
        return '0 0.6rem 0.6rem 0';
    return '0';
}};

    &::after {
      background: transparent;
    }
  }
`;
const StyledInput = styled.input `
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  cursor: inherit;

  &:focus {
    outline: none;
  }
`;
const StyledCaption = styled(Caption) `
  text-align: center;
  user-select: none;
  white-space: nowrap;
  color: ${({ $isSelected }) => ($isSelected ? 'white' : 'inherit')};
  font-weight: ${({ $isSelected, theme }) => ($isSelected ? theme.fontWeights.semibold : 'inherit')};
`;
